import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import * as yup from "yup"
import {useFormik} from "formik"
import {Button, Modal, Table} from "antd"
import {PlusOutlined} from "@ant-design/icons"
import {FormSelect} from "../../components/Form/FormSelect"
import {center} from "../../helps/tableHelper"
import {AttributesVersionsResponse} from "../../models/response"
import {
    closestCenter,
    CollisionDetection,
    DndContext,
    getFirstCollision,
    MeasuringStrategy,
    Modifiers,
    MouseSensor,
    pointerWithin,
    rectIntersection,
    TouchSensor,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from "@dnd-kit/core"
import {
    AnimateLayoutChanges,
    arrayMove,
    defaultAnimateLayoutChanges,
    SortableContext,
    SortingStrategy,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import {Container, ContainerProps} from "./Container"
import {CSS} from "@dnd-kit/utilities"
import {unstable_batchedUpdates} from "react-dom"
import {Item} from "./Item"
import {OptionType} from "../../types/types"

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
    defaultAnimateLayoutChanges({...args, wasDragging: true})

type Items = Record<UniqueIdentifier, UniqueIdentifier[]>;

interface Props
{
    getItemStyles?(args: {
        value: UniqueIdentifier;
        index: number;
        overIndex: number;
        isDragging: boolean;
        containerId: UniqueIdentifier;
        isSorting: boolean;
        isDragOverlay: boolean;
    }): React.CSSProperties;

    wrapperStyle?(args: { index: number }): React.CSSProperties;

    itemCount?: number;
    items?: Items;
    handle?: boolean;
    strategy?: SortingStrategy;
    modifiers?: Modifiers;
    scrollable?: boolean;
    vertical?: boolean;
}

const empty: UniqueIdentifier[] = []

const columnsTable = [
    {
        title: "Атрибут",
        dataIndex: "key",
        key: "key",
        align: center,
    },
    {
        title: "Описание",
        dataIndex: "value",
        key: "value",
        align: center,
    },
    {
        title: "По умолчанию",
        dataIndex: "defaultValue",
        key: "defaultValue",
        align: center,
    },
]

export type GroupVersionAttributeType = {
    index: number
    id: number
    name: string
    attributes: Array<VersionAttributeType>
}

export type VersionAttributeType = {
    index: number
    key: string
    value: string
    defaultValue: string
    options: Array<OptionType>
}

type PropsTypes = {
    attributes?: string | null
    setAttributes: (value: string) => void
    setSubmitCallback: (value: any) => void
    versions: AttributesVersionsResponse | null
}

type ValuesType = {
    [key: string]: {
        index: number
        value: string
        attributes: {
            [key: string]: {
                index: number
                key: string
                value: string
                defaultValue: string
                options: {
                    [key: string]: {
                        value: number
                        label: string
                    }
                },
            }
        }
    }
}

export const JsonAttributes: React.FC<PropsTypes & Props> = ({
                                                                 attributes,
                                                                 setAttributes,
                                                                 setSubmitCallback,
                                                                 versions,
                                                                 handle = false,
                                                                 getItemStyles = () => ({}),
                                                                 strategy = verticalListSortingStrategy,
                                                                 scrollable,
                                                             }) =>
{
    const [attrs, setAttrs] = useState<Items>({})
    const [attrGroups, setAttrGroups] = useState(Object.keys(attrs) as UniqueIdentifier[])
    const [attrCount, setAttrCount] = useState(0)
    const [attrGroupCount, setAttrGroupCount] = useState(0)
    const [schema, setSchema] = useState<any>(yup.object().shape({}))
    const [selectedVersion, setSelectedVersion] = useState<any>(null)
    const [attributesToCopy, setAttributesToCopy] = useState<Array<GroupVersionAttributeType> | null>(null)
    const [showModal, setShowModal] = useState(false)
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
    const lastOverId = useRef<UniqueIdentifier | null>(null)
    const recentlyMovedToNewContainer = useRef(false)
    const scrollRef = useRef<any>()
    const isSortingContainer = activeId ? attrGroups.includes(activeId) : false
    const [hideAttrs, setHideAttrs] = useState(false)

    const collisionDetectionStrategy: CollisionDetection = useCallback(
        (args) =>
        {
            if (activeId && activeId in attrs)
            {
                return closestCenter({
                    ...args,
                    droppableContainers: args.droppableContainers.filter(
                        (container) => container.id in attrs,
                    ),
                })
            }

            // Start by finding any intersecting droppable
            const pointerIntersections = pointerWithin(args)
            const intersections =
                pointerIntersections.length > 0
                    ? // If there are droppables intersecting with the pointer, return those
                    pointerIntersections
                    : rectIntersection(args)
            let overId = getFirstCollision(intersections, "id")

            if (overId != null)
            {
                if (overId in attrs)
                {
                    const containerItems = attrs[overId]

                    // If a container is matched and it contains attrs (columns 'A', 'B', 'C')
                    if (containerItems.length > 0)
                    {
                        // Return the closest droppable within that container
                        overId = closestCenter({
                            ...args,
                            droppableContainers: args.droppableContainers.filter(
                                (container) =>
                                    container.id === overId &&
                                    containerItems.includes(container.id),
                            ),
                        })[0]?.id
                    }
                }

                lastOverId.current = overId

                return [{id: overId}]
            }

            // When a draggable item moves to a new container, the layout may shift
            // and the `overId` may become `null`. We manually set the cached `lastOverId`
            // to the id of the draggable item that was moved to the new container, otherwise
            // the previous `overId` will be returned which can cause attrs to incorrectly shift positions
            if (recentlyMovedToNewContainer.current)
            {
                lastOverId.current = activeId
            }

            // If no droppable is matched, return the last match
            return lastOverId.current ? [{id: lastOverId.current}] : []
        },
        [activeId, attrs],
    )
    const [clonedItems, setClonedItems] = useState<Items | null>(null)
    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor),
    )
    const findContainer = (id: UniqueIdentifier) =>
    {
        if (id in attrs)
        {
            return id
        }

        return Object.keys(attrs).find((key) => attrs[key].includes(id))
    }

    const getIndex = (id: UniqueIdentifier) =>
    {
        const container = findContainer(id)

        if (!container)
        {
            return -1
        }

        return attrs[container].indexOf(id)
    }

    const onDragCancel = () =>
    {
        if (clonedItems)
        {
            // Reset attrs to their original state in case attrs have been
            // Dragged across attrGroups
            setAttrs(clonedItems)
        }

        setActiveId(null)
        setClonedItems(null)
    }

    useEffect(() =>
    {
        requestAnimationFrame(() =>
        {
            recentlyMovedToNewContainer.current = false
        })
    }, [attrs])

    const versionOptions = useMemo(() => versions
        ?.data?.map(i => ({value: i.version, label: `Версия ${i.version} : ${i.active ? "Активно" : "Не активно"}`})) || [], [versions])

    const formik = useFormik({
        initialValues: {
            "0": {
                index: 0,
                value: "Без группы",
                attributes: {
                    "0": {
                        index: 0,
                        key: "",
                        value: "",
                        defaultValue: "",
                        options: {},
                    },
                },
            },
        } as ValuesType,
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: () =>
        {
        },
    })
    const {values, handleChange, handleBlur, errors, touched, setFieldValue, validateForm, setTouched, setValues} = formik

    const addGroup = () =>
    {
        unstable_batchedUpdates(() =>
        {
            const newContainerId = `group${attrGroupCount + 1}`

            setAttrGroups((containers) => [...containers, newContainerId])

            setAttrs((items) => ({
                ...items,
                [newContainerId]: [],
            }))

            const maxGroupIndex = Math.max(...Object.values(values).map(i => i.index))
            const newGroup = {
                index: maxGroupIndex + 1,
                value: `Новая группа ${attrGroupCount + 1}`,
                attributes: {} as ValuesType[string]["attributes"],
            }

            setFieldValue(`${attrGroupCount + 1}`, newGroup)

            setAttrGroupCount(prev => prev + 1)
            setActiveId(null)
        })
    }
    
    const removeGroup = (key: string) =>
    {
        unstable_batchedUpdates(() =>
        {
            const id = key.replace("group", "")

            const noGroupAttrs = {...values["0"].attributes}
            const oldGroupAttrs = {...values[id].attributes}

            Object.values(noGroupAttrs)
                .forEach((a, index) =>
                {
                    a.index = index
                })

            const noGroupLength = Object.values(noGroupAttrs).length

            Object.values(oldGroupAttrs)
                .forEach((a, index) =>
                {
                    a.index = index + noGroupLength
                })

            const newValue = {
                ...values["0"],
                attributes: {...noGroupAttrs, ...oldGroupAttrs},
            }

            setFieldValue("0", newValue)

            setAttrGroups((containers) => [...containers.filter(i => i !== key)])

            setAttrs((items) =>
            {
                const {[key]: values, ...rest} = items
                rest["group0"].push(...values)
                return rest
            })

            setFieldValue(id, undefined)
        })
    }

    const addAttribute = (groupKey?: UniqueIdentifier) =>
    {
        unstable_batchedUpdates(() =>
        {
            const id = groupKey ? parseInt(groupKey?.toString().replace("group", "")) : 0

            const group = groupKey?.toString() || "group0"

            setAttrs((items) => ({
                ...items,
                [group]: [...items[group], `attr${attrCount}`],
            }))

            const attrIndexes: Array<number> = []

            Object.values(values[id].attributes).forEach(a =>
            {
                attrIndexes.push(a.index)
            })

            const maxAttrIndex = Math.max(...attrIndexes)

            setFieldValue(`${id}.attributes.${attrCount}`, {
                index: isFinite(maxAttrIndex) ? maxAttrIndex + 1 : 0,
                key: "",
                value: "",
                defaultValue: "",
                options: {},
            })

            setAttrCount(prev => prev + 1)
        })
    }

    const removeAttribute = (group: string, attr: string) =>
    {
        unstable_batchedUpdates(() =>
        {
            const idGroup = group.replace("group", "")
            const idAttr = attr.replace("attr", "")

            setAttrs((items) => ({
                ...items,
                [group]: items[group]?.filter((id) => id !== attr),
            }))

            setFieldValue(`${idGroup}.attributes.${idAttr}`, undefined)
        })
    }

    useEffect(() =>
    {
        const groups: Array<GroupVersionAttributeType> = []

        for (const key in values)
        {
            const group = key.replace("group", "")
            const index = values[group].index
            const name = values[group].value
            const attributes: Array<VersionAttributeType> = []

            Object.values(values[group].attributes).forEach(a =>
            {
                const attr: VersionAttributeType = {
                    index: a.index,
                    key: a.key,
                    value: a.value,
                    defaultValue: a.defaultValue,
                    options: [],
                }

                Object.values(a.options).forEach(o =>
                {
                    attr.options.push({
                        value: o.value,
                        label: o.label,
                    })
                })

                attributes.push(attr)
            })

            groups.push({id: parseInt(group), index, name, attributes})
        }

        setAttributes(JSON.stringify(groups))
    }, [values])

    const validate = useCallback(async (values: any) =>
    {
        const valid = await validateForm(values)
        const keys = Object.keys(valid)
        const touched: any = {}

        keys.forEach(key =>
        {
            touched[key] = true
        })

        await setTouched(values)
        return keys.length === 0
    }, [validateForm, setTouched])
    
    useEffect(() =>
    {
        const schema: Record<string, yup.ObjectSchema<any>> = {}

        for (const groupKey in values)
        {
            const group = values[groupKey]

            const attrSchema: Record<string, yup.ObjectSchema<any>> = {}

            for (const attrKey in group.attributes)
            {
                const attr = group.attributes[attrKey]

                const optionsSchema = yup.lazy((opt) =>
                    yup.object(
                        Object.fromEntries(
                            Object.keys(opt || {}).map((optionKey) => [
                                optionKey,
                                yup.object({
                                    value: yup.number().required("value is required"),
                                    label: yup.string().required("label is required"),
                                }),
                            ]),
                        ),
                    ),
                )

                attrSchema[attrKey] = yup.object({
                    key: yup.string().required(),
                    value: yup.string().required(),
                    defaultValue: yup.string().required(),
                    options: optionsSchema,
                })
            }

            schema[groupKey] = yup.object({
                value: yup.string().required(),
                attributes: yup.object(attrSchema),
            })
        }

        setSchema(yup.object(schema))

        setSubmitCallback(() => () => validate(values))
    }, [values])

    const setFields = useCallback((attributesArray: Array<GroupVersionAttributeType>) =>
    {
        const groups: Record<UniqueIdentifier, number> = {}
        const items: Record<UniqueIdentifier, Array<{ key: string, index: number }>> = {}
        const itemsToSet: Record<UniqueIdentifier, Array<UniqueIdentifier>> = {}
        const newValues: ValuesType = {}

        groups["group0"] = 0
        itemsToSet["group0"] = []

        newValues[`0`] = {
            index: 0,
            value: "Без группы",
            attributes: {},
        }

        if (attributesArray)
        {
            let maxGroupId = Math.max(...attributesArray.map(i => i.id))
            let attrCount = 0

            attributesArray
                .sort((a, b) => a.index - b.index)
                .forEach((g, groupIndex) =>
                {
                    g.index = groupIndex

                    groups[`group${g.id}`] = g.index
                    itemsToSet[`group${g.id}`] = []
                    items[`group${g.id}`] = []

                    const attributes: ValuesType[string]["attributes"] = {}

                    g.attributes
                        .sort((a, b) => a.index - b.index)
                        .forEach((a, attrIndex) =>
                        {
                            a.index = attrIndex

                            items[`group${g.id}`].push({key: `attr${attrCount}`, index: a.index})

                            const attrValue: ValuesType[string]["attributes"][string] = {
                                index: a.index,
                                key: a.key,
                                value: a.value,
                                defaultValue: a.defaultValue,
                                options: {},
                            }

                            let optNumberKey = 0

                            if (a.options && a.options.length > 0)
                            {
                                for (const opt of a.options)
                                {
                                    attrValue.options[`${optNumberKey}`] = {
                                        value: parseInt(opt.value.toString()),
                                        label: opt.label,
                                    }
                                    optNumberKey++
                                }
                            }

                            attributes[`${attrCount}`] = attrValue

                            attrCount++
                        })

                    newValues[`${g.id}`] = {
                        index: g.index,
                        value: g.name,
                        attributes: attributes,
                    }
                })

            setAttrCount(attrCount)
            setAttrGroupCount(maxGroupId)
        }

        for (const groupId in items)
        {
            items[groupId].sort((a, b) => a.index - b.index)
            itemsToSet[groupId] = items[groupId].map(i => i.key)
        }

        setAttrGroups(Object.keys(groups).sort((a, b) => groups[a] - groups[b]).map(i => i))
        setAttrs(itemsToSet)

        setValues(newValues)
    }, [setValues])

    useEffect(() =>
    {
        if (attributes)
        {
            const parsed: Array<GroupVersionAttributeType> = JSON.parse(attributes)
            if (parsed) setFields(parsed)
        }
    }, [attributes])

    useEffect(() =>
    {
        if (selectedVersion && versions)
        {
            const version = versions.data.find(i => i.version === selectedVersion)

            if (version)
            {
                const parsed: Array<GroupVersionAttributeType> = JSON.parse(version.attributes)

                if (parsed)
                {
                    setAttributesToCopy(parsed)
                }
            }
        }
    }, [selectedVersion, versions])

    const copyVersion = useCallback(() =>
    {
        if (attributesToCopy)
        {
            setFields(attributesToCopy)
            setShowModal(false)
        }
    }, [attributesToCopy])

    const attributesVersionToCopyData = useMemo(() =>
        {
            if (!attributesToCopy) return []

            const dict: Record<number, {
                groupId: number,
                groupName: string,
                groupIndex: number,
                attrs: Array<{
                    index: number,
                    key: string,
                    value: string,
                    defaultValue: string | number
                }>
            }> = []

            dict[0] = {
                groupId: 0,
                groupName: "Без группы",
                groupIndex: 0,
                attrs: [],
            }

            attributesToCopy.forEach(g =>
            {
                dict[g.id] = {
                    groupId: g.id,
                    groupName: g.name,
                    groupIndex: g.index,
                    attrs: [],
                }

                g.attributes.forEach(a =>
                {
                    dict[g.id].attrs.push({
                        index: a.index,
                        key: a.key,
                        value: a.value,
                        defaultValue: a.options?.find(i => i.value === a.defaultValue)?.label ?? a.defaultValue,
                    })
                })
            })

            return Object.values(dict).sort((a, b) => a.groupIndex - b.groupIndex)
        },
        [attributesToCopy])

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetectionStrategy}
            measuring={{
                droppable: {
                    strategy: MeasuringStrategy.Always,
                },
            }}
            onDragStart={({active, activatorEvent}) =>
            {
                if (active.id in attrs) setHideAttrs(true)
                setActiveId(active.id)
                setClonedItems(attrs)
                scrollRef.current = activatorEvent.target
            }}
            onDragOver={({active, over}) =>
            {
                if (over?.id == null)
                    return

                // Drag Group
                if (active.id in attrs)
                {
                    if (active.id !== over.id)
                    {
                        let newAttrGroups: UniqueIdentifier[] = []

                        setAttrGroups((containers) =>
                        {
                            const activeIndex = containers.indexOf(active.id)
                            const overIndex = containers.indexOf(over.id)

                            newAttrGroups = arrayMove(containers, activeIndex, overIndex)
                            return newAttrGroups
                        })

                        newAttrGroups.forEach((groupKey, index) =>
                        {
                            try
                            {
                                const groupId = parseInt(groupKey.toString().replace("group", ""))

                                if (values[groupId])
                                {
                                    setFieldValue(`${groupId}.index`, index)
                                }
                            } catch (e)
                            {
                                //
                            }
                        })
                    }
                }
                else
                {
                    //Drag attribute
                    if (active.id !== over.id)
                    {
                        const activeContainer = findContainer(active.id)

                        if (activeContainer == null)
                            return

                        const overContainer = findContainer(over.id)

                        if (overContainer == null)
                            return

                        let newAttrs: Items | undefined

                        // Drag to new group
                        if (activeContainer !== overContainer)
                        {
                            setAttrs((items) =>
                            {
                                const activeItems = items[activeContainer]
                                const overItems = items[overContainer]
                                const overIndex = overItems.indexOf(over.id)
                                const activeIndex = activeItems.indexOf(active.id)

                                const overCenter = over.rect.top + over.rect.height / 2;
                                const isBelowOverItem =
                                    over &&
                                    active.rect.current.translated &&
                                    active.rect.current.translated.top > overCenter;

                                const modifier = isBelowOverItem ? 1 : 0;

                                const newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length;

                                recentlyMovedToNewContainer.current = true

                                // Удаляем элемент из текущего контейнера
                                const newActiveItems = activeItems.filter((item) => item !== active.id);

                                // Вставляем элемент в правильную позицию в новом контейнере
                                const newOverItems = [
                                    ...overItems.slice(0, newIndex),
                                    activeItems[activeIndex],
                                    ...overItems.slice(newIndex),
                                ];

                                newAttrs = {
                                    ...items,
                                    [activeContainer]: newActiveItems,
                                    [overContainer]: newOverItems,
                                }

                                return newAttrs
                            })

                            if (newAttrs)
                            {
                                const activeContainerId = activeContainer.toString().replace("group", "")
                                const overContainerId = overContainer.toString().replace("group", "")
                                const activeNumber = active.id.toString().replace("attr", "")

                                if (values[activeContainerId].attributes[activeNumber])
                                {
                                    const newIndex = newAttrs[overContainer].indexOf(active.id)
                                    const newAttr = {...values[activeContainerId].attributes[activeNumber], index: newIndex}
                                    if (!newAttr?.options)
                                        debugger
                                    setFieldValue(`${activeContainerId}.attributes.${activeNumber}`, undefined)
                                    setFieldValue(`${overContainerId}.attributes.${activeNumber}`, newAttr)
                                }
                            }
                        }
                        // Drag inside the group
                        else
                        {
                            const activeIndex = attrs[activeContainer].indexOf(active.id)
                            const overIndex = attrs[overContainer].indexOf(over.id)

                            if (activeIndex !== overIndex)
                            {
                                setAttrs((items) =>
                                {
                                    newAttrs = ({
                                        ...items,
                                        [overContainer]: arrayMove(
                                            items[overContainer],
                                            activeIndex,
                                            overIndex,
                                        ),
                                    })

                                    return newAttrs
                                })
                            }
                        }

                        if (newAttrs)
                        {
                            for (const groupKey in newAttrs)
                            {
                                if (groupKey === activeContainer || groupKey === overContainer) {
                                    const groupId = parseInt(groupKey.toString().replace("group", ""))

                                    if (values[groupId]?.attributes)
                                    {
                                        Object.values(newAttrs[groupKey]).forEach((attr, index) =>
                                        {
                                            const attrId = parseInt(attr.toString().replace("attr", ""))

                                            if (values[groupId]?.attributes[attrId])
                                            {
                                                setFieldValue(`${groupId}.attributes.${attrId}.index`, index)
                                            }
                                        })
                                    }
                                }
                            }
                        }
                    }
                }
            }}
            onDragEnd={({active, over}) =>
            {
                const activeContainer = findContainer(active.id)

                if (!activeContainer)
                {
                    setActiveId(null)
                    return
                }

                if (!over?.id)
                {
                    setActiveId(null)
                    return
                }

                const overContainer = findContainer(over.id)

                for (const groupKey in attrs)
                {
                    if (groupKey === activeContainer || groupKey === overContainer) {
                        const groupId = parseInt(groupKey.toString().replace("group", ""))

                        if (values[groupId]?.attributes)
                        {
                            Object.values(attrs[groupKey]).forEach((attr, index) =>
                            {
                                const attrId = parseInt(attr.toString().replace("attr", ""))

                                if (values[groupId]?.attributes[attrId])
                                {
                                    setFieldValue(`${groupId}.attributes.${attrId}.index`, index)
                                }
                            })
                        }
                    }
                }

                setActiveId(null)
                setHideAttrs(false)

                if (hideAttrs)
                {
                    setTimeout(() => scrollRef?.current?.scrollIntoView({
                        block: "center",
                        inline: "center",
                    }), 10)
                }
            }}
            onDragCancel={onDragCancel}
        >
            <div style={{display: "flex", flexDirection: "column", justifyContent: "center"}}>
                <div className="description-title" style={{fontSize: 16, marginTop: 20}}>Атрибуты</div>

                <Button type="link"
                        onClick={() => setShowModal(true)}
                        style={{marginBottom: 10, alignSelf: "center"}}
                >
                    Скопировать атрибуты с другой версии
                </Button>

                {
                    showModal
                        ? <Modal title="Скопировать атрибуты с другой версии"
                                 open={showModal}
                                 onOk={() => copyVersion()}
                                 okText="Скопировать"
                                 onCancel={() => setShowModal(false)}
                                 width={"fit-content"}
                                 style={{marginTop: 20, marginBottom: 20, minWidth: 400}}
                        >
                            <div style={{width: 250, margin: "20px auto"}}>
                                <FormSelect placeholder="Версия"
                                            value={selectedVersion}
                                            onChange={(value) => setSelectedVersion(value)}
                                            options={versionOptions}
                                            className="form-select"

                                />
                            </div>

                            <div className="table-responsive">
                                {
                                    attributesVersionToCopyData ?
                                        attributesVersionToCopyData.map(i => (
                                            <Table key={i.groupId}
                                                   columns={columnsTable}
                                                   title={() => <div style={{fontSize: 16, fontWeight: "bold", margin: "auto"}}>{i.groupName}</div>}
                                                   dataSource={i.attrs.sort((a, b) => a.index - b.index)}
                                                   pagination={false}
                                                   bordered
                                                   className="ant-border-space no-hover"
                                                   style={{margin: "10px 0"}}
                                            />
                                        ))

                                        : null
                                }
                            </div>
                        </Modal>
                        : null
                }
            </div>

            <div style={{display: "flex", flexDirection: "column", gap: 20}}>
                <SortableContext
                    items={attrGroups}
                    strategy={verticalListSortingStrategy}
                >
                    {attrGroups.map((containerId) => (
                        <DroppableContainer
                            key={containerId}
                            id={containerId}
                            label={containerId?.toString()}
                            columns={1}
                            items={attrs[containerId]}
                            scrollable={scrollable}
                            onRemove={() => removeGroup(containerId as string)}
                            values={groupKeyFilter(values, containerId)}
                            errors={groupKeyFilter(errors, containerId)}
                            touched={groupKeyFilter(touched, containerId)}
                            handleBlur={handleBlur}
                            handleChange={handleChange}
                        >
                            {
                                !hideAttrs
                                    ? <SortableContext items={attrs[containerId] ?? []} strategy={strategy}>
                                        {attrs[containerId]?.map((value, index) =>
                                        {
                                            return (
                                                <SortableItem
                                                    disabled={isSortingContainer}
                                                    key={value}
                                                    id={value}
                                                    handle={handle}
                                                    style={getItemStyles}
                                                    containerId={containerId}
                                                    values={attrKeyFilter(values, containerId, value)}
                                                    errors={attrKeyFilter(errors, containerId, value)}
                                                    touched={attrKeyFilter(touched, containerId, value)}
                                                    handleBlur={handleBlur}
                                                    handleChange={handleChange}
                                                    setFieldValue={setFieldValue}
                                                    getIndex={getIndex}
                                                    onRemove={() =>
                                                    {
                                                        removeAttribute(containerId as string, value as string)
                                                    }}
                                                />
                                            )
                                        })}

                                        <Button type="dashed"
                                                style={{marginTop: 15}}
                                                onClick={() => addAttribute(containerId)}
                                                icon={<PlusOutlined/>}>
                                            Добавить поле
                                        </Button>
                                    </SortableContext>
                                    : null
                            }
                        </DroppableContainer>
                    ))}

                    {
                        !hideAttrs
                            ? <Button type="dashed"
                                      style={{marginTop: 15, height: 100}}
                                      onClick={() => addGroup()}
                                      icon={<PlusOutlined/>}>
                                Добавить группу
                            </Button>
                            : null
                    }
                </SortableContext>
            </div>
        </DndContext>
    )
}


export const DroppableContainer = memo(({
                                            children,
                                            columns = 1,
                                            disabled,
                                            id,
                                            items,
                                            style,
                                            values,
                                            errors,
                                            touched,
                                            handleBlur,
                                            handleChange,
                                            ...props
                                        }: ContainerProps & {
    disabled?: boolean;
    id: UniqueIdentifier;
    items: UniqueIdentifier[];
    style?: React.CSSProperties;
    values: any
    errors: any
    touched: any
    handleBlur: any
    handleChange: any
}) =>
{
    const {
        active,
        attributes,
        isDragging,
        listeners,
        over,
        setNodeRef,
        transition,
        transform,
    } = useSortable({
        id,
        data: {
            type: "container",
            children: items,
        },
        animateLayoutChanges,
    })

    const isOverContainer = over
        ? (id === over.id && active?.data.current?.type !== "container") ||
        items?.includes(over.id)
        : false

    return (
        <Container
            ref={disabled ? undefined : setNodeRef}
            style={{
                ...style,
                transition,
                transform: CSS.Translate.toString(transform),
                opacity: isDragging ? 0.5 : undefined,
            }}
            hover={isOverContainer}
            handleProps={{
                ...attributes,
                ...listeners,
            }}
            columns={columns}
            id={id}
            values={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            {...props}
        >
            {children}
        </Container>
    )
})

interface SortableItemProps
{
    containerId: UniqueIdentifier;
    id: UniqueIdentifier;
    handle: boolean;
    disabled?: boolean;

    style(args: any): React.CSSProperties;

    getIndex(id: UniqueIdentifier): number;

    values: any
    errors: any
    touched: any
    handleBlur: any
    handleChange: any
    setFieldValue: any
    onRemove: any
}

export const SortableItem = memo(({
                                      disabled,
                                      id,
                                      handle,
                                      style,
                                      containerId,
                                      getIndex,
                                      values,
                                      errors,
                                      touched,
                                      handleBlur,
                                      handleChange,
                                      setFieldValue,
                                      onRemove,
                                  }: SortableItemProps) =>
{
    const {
        setNodeRef,
        setActivatorNodeRef,
        listeners,
        isDragging,
        isSorting,
        over,
        overIndex,
        transform,
        transition,
    } = useSortable({
        id,
    })
    const mounted = useMountStatus()
    const mountedWhileDragging = isDragging && !mounted

    return (
        <Item
            ref={disabled ? undefined : setNodeRef}
            value={id}
            containerId={containerId}
            dragging={isDragging}
            sorting={isSorting}
            handle={handle}
            handleProps={handle ? {ref: setActivatorNodeRef} : undefined}
            style={style({
                value: id,
                isDragging,
                isSorting,
                overIndex: over ? getIndex(over.id) : overIndex,
                containerId,
            })}
            transition={transition}
            transform={transform}
            fadeIn={mountedWhileDragging}
            listeners={listeners}
            values={values}
            errors={errors}
            touched={touched}
            handleBlur={handleBlur}
            handleChange={handleChange}
            id={id}
            setFieldValue={setFieldValue}
            onRemove={onRemove}/>
    )
})

function useMountStatus()
{
    const [isMounted, setIsMounted] = useState(false)

    useEffect(() =>
    {
        const timeout = setTimeout(() => setIsMounted(true), 500)

        return () => clearTimeout(timeout)
    }, [])

    return isMounted
}

function groupKeyFilter(values: any, group: UniqueIdentifier)
{
    const id = group?.toString()?.replace("group", "")
    return values[id] ?? {}
}

function attrKeyFilter(values: any, group: UniqueIdentifier, attr: UniqueIdentifier)
{
    const idGroup = group?.toString()?.replace("group", "")
    const idAttr = attr?.toString()?.replace("attr", "")
    if (!values[idGroup] || !values[idGroup].attributes) return {}
    return values[idGroup].attributes[idAttr] ?? {}
}